import React, { Fragment, useEffect, useState } from "react";
import PageHeader from "../common/components/PageHeader";
import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import { NotificationPriorityType, UserTypeId } from "../common/const";
import CommonCard from "../common/components/CommonCard";
import { useTheme } from "@emotion/react";
import CommonButton from "../common/components/CommonButton";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../common/components/LoadingSpinner";
import { setLoading } from "../features/authSlice";
import { getAllNotification, getNotificationStat, markNotificationRead } from "../api/notificationApi";
import { showNotification } from "../common/components/Notification/ToastifyNotification";
import moment from "moment";

const Notification = () => {
    const dispatch = useDispatch();
    const { loading, user } = useSelector((state) => state.auth);
    const theme = useTheme();
    const [filter, setFilter] = useState('total');
    const [notifications, setNotifications] = useState([]);
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [notificationStat, setNotificationStat] = useState(null);

    const getNotificationStatData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getNotificationStat();
            setNotificationStat(response.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    const getAllNotificationData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getAllNotification(user?.user_id, filter === 'highpriority' ? 'HIGH' : '');
            setNotifications(response.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        getNotificationStatData();
        if (user?.user_id) {
            getAllNotificationData();
        }
    }, [filter, user]);

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <PageHeader
                title="Notifications"
                content={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                            Stay updated on important notifications
                        </Typography>
                    </Stack>
                }
            />
            <Box
                mt={1}
                sx={{
                    height: '75vh',
                    overflow: 'auto'
                }}
            >
                <CommonCard
                    content={
                        <Box
                            display="flex"
                            gap={1}
                            width={isTablet ? "100%" : "50%"}
                        >
                            <CommonButton
                                label={`Total Alert (${notificationStat?.total})`}
                                size="small"
                                height="35px"
                                marginBottom="0px"
                                fontWeight="bold"
                                width={isMobile ? "35%" : "20%"}
                                background={filter === 'total' ? theme.palette.primary.main : theme.palette.background.main}
                                color={filter === 'total' ? theme.palette.secondary.main : theme.palette.background.dark}
                                handleClick={() => {
                                    setFilter('total');
                                }}
                                border={filter !== 'total' ? "1px solid lightgray" : 'none'}
                            />
                            <CommonButton
                                label={`Unread (${notificationStat?.unread})`}
                                size="small"
                                height="35px"
                                marginBottom="0px"
                                fontWeight="bold"
                                width={isMobile ? "30%" : "20%"}
                                background={filter === 'unread' ? theme.palette.primary.main : theme.palette.background.main}
                                color={filter === 'unread' ? theme.palette.secondary.main : theme.palette.background.dark}
                                handleClick={() => {
                                    setFilter('unread');
                                }}
                                border={filter !== 'unread' ? "1px solid lightgray" : 'none'}
                            />
                            <CommonButton
                                label={`High Priority (${notificationStat?.highPriority})`}
                                size="small"
                                height="35px"
                                marginBottom="0px"
                                fontWeight="bold"
                                width={isMobile ? "35%" : "20%"}
                                background={filter === 'highpriority' ? theme.palette.primary.main : theme.palette.background.main}
                                color={filter === 'highpriority' ? theme.palette.secondary.main : theme.palette.background.dark}
                                handleClick={() => {
                                    setFilter('highpriority');
                                }}
                                border={filter !== 'highpriority' ? "1px solid lightgray" : 'none'}
                            />
                        </Box>
                    }
                    marginTop={1.5}
                    padding="12px"
                    paddingBottom="2px"
                    borderRadius={1}
                />
                <Typography sx={{ fontSize: 16, fontWeight: 800, mt: 1 }}>
                    Unread ({filter === 'highpriority' ? notifications?.unread_notifications?.length : notificationStat?.unread})
                </Typography>
                <Box
                    sx={{
                        height: '28vh', // [mobile, tablet, desktop]
                        overflow: 'auto', // Add scroll if content exceeds height
                    }}
                >
                    {notifications?.unread_notifications?.length > 0 ? notifications?.unread_notifications?.map((a, index) => (
                        <CommonCard
                            key={index}
                            content={
                                <Box
                                    display="flex"
                                    flexDirection={isMobile ? 'column' : 'row'}
                                    justifyContent="space-between"
                                >
                                    <Box
                                        display='flex'
                                        gap={1}
                                    >
                                        <Box>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: '10px'
                                                }}
                                            >
                                                <Box>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{a.title}</Typography>
                                                    <Typography sx={{ fontSize: 11 }} color={theme.palette.background.dark}>{a.published_at ? moment(a.published_at).format('YYYY-MM-DD') : ''}</Typography>
                                                </Box>
                                                {a.priority && (
                                                    <Box
                                                        sx={{
                                                            background: NotificationPriorityType[a.priority] === 'High Priority' || NotificationPriorityType[a.priority] === 'Urgent' ? theme.palette.primary.main : theme.palette.secondary.default,
                                                            textAlign: 'center',
                                                            borderRadius: 0.8,
                                                            width: 80,
                                                            height: 21,
                                                            padding: 0.4
                                                        }}
                                                    >
                                                        <Typography sx={{ color: theme.palette.secondary.main, fontSize: 10 }}>{NotificationPriorityType[a.priority]}</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box
                                        display='flex'
                                        gap={1}
                                        width={isMobile ? '60%' : isTablet ? "30%" : "15%"}
                                        mt={isMobile ? 2 : 0}
                                    >
                                        {user?.user_type_id === UserTypeId.Student && (
                                            <CommonButton
                                                label="Mark as read"
                                                size="small"
                                                height="30px"
                                                background={theme.palette.background.main}
                                                color="black"
                                                marginBottom={isMobile ? '0px' : 3}
                                                handleClick={async () => {
                                                    try {
                                                        dispatch(setLoading({
                                                            loading: true
                                                        }));
                                                        const response = await markNotificationRead({
                                                            "notification_ids": [a.notification_id]
                                                        });
                                                        if (response.success) {
                                                            showNotification(response.message, 'success');
                                                            dispatch(setLoading({
                                                                loading: false
                                                            }));
                                                            getNotificationStatData();
                                                            getAllNotificationData();
                                                        }
                                                    } catch (err) {
                                                        const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                                        showNotification(errorMessage, 'error');
                                                        dispatch(setLoading({
                                                            loading: false
                                                        }));
                                                    }
                                                }}
                                            />)}
                                    </Box>
                                </Box>
                            }
                            marginTop={1.5}
                            padding="12px"
                            borderRadius={1}
                            background="#E0E8F5"
                        />
                    )) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 0.8 }}>
                            <Typography>No Data Found</Typography>
                        </Box>
                    )}
                </Box>
                {filter !== 'unread' && (
                    <Fragment>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, mt: 1 }}>
                            All Notifications ({filter === 'highpriority' ? notifications?.all?.length : notificationStat?.total})
                        </Typography>
                        <Box
                            sx={{
                                height: '32vh', // [mobile, tablet, desktop]
                                overflow: 'auto', // Add scroll if content exceeds height
                            }}
                        >
                            {notifications?.all?.length > 0 ? notifications?.all?.map((a, index) => (
                                <CommonCard
                                    key={index}
                                    content={
                                        <Box
                                            display='flex'
                                            justifyContent='space-between'
                                            flexDirection={isMobile ? 'column' : 'row'}
                                        >
                                            <Box
                                                display='flex'
                                                gap={1}
                                            >
                                                <Box>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: '10px'
                                                        }}
                                                    >
                                                        <Box>
                                                            <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{a?.title}</Typography>
                                                            <Typography sx={{ fontSize: 11 }} color={theme.palette.background.dark}>{a?.published_at ? moment(a?.published_at).format('YYYY-MM-DD') : ''}</Typography>
                                                        </Box>
                                                        {a.priority && (
                                                            <Box
                                                                sx={{
                                                                    background: NotificationPriorityType[a?.priority] === 'High Priority' || NotificationPriorityType[a?.priority] === 'Urgent' ? theme.palette.primary.main : theme.palette.secondary.default,
                                                                    textAlign: 'center',
                                                                    borderRadius: 0.8,
                                                                    width: 80,
                                                                    height: 21,
                                                                    padding: 0.4
                                                                }}
                                                            >
                                                                <Typography sx={{ color: theme.palette.secondary.main, fontSize: 10 }}>{NotificationPriorityType[a?.priority]}</Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    }
                                    marginTop={1.5}
                                    padding="12px"
                                    borderRadius={1}
                                />
                            )) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 0.8 }}>
                                    <Typography>No Data Found</Typography>
                                </Box>
                            )}
                        </Box>
                    </Fragment>
                )}
            </Box>
        </Fragment>
    )
}

export default Notification;